/** "Batch actions" split into a toolbar Button + Menu, plus the confirm / custom
 *  dialog each action drives. Rendered inside GridControlsToolbar and fed by
 *  ResourceDataGrid; visible only when a selection is active.
 *
 *  Per action, on click:
 *   - `dialog` present  → render the custom dialog;
 *   - else `run`        → call it directly (no confirm);
 *   - else              → a ConfirmDialog (destructive-styled when flagged)
 *                         using `confirmText(count)`, then `runStandard()`.
 *  Only actions the user is permitted to run are shown. One the selection rules
 *  out (`unavailableReason`) stays in the menu, disabled, and says why on its
 *  second line — still reachable by keyboard so the reason is read out.
 */
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { useState, type ReactNode } from 'react';

import type { BatchSelection } from '@/api/batch';
import { usePermissions } from '@/hooks/usePermissions';

import { pinRelativeFilters } from './pinRelativeFilters';

import { BatchConfirmDialog } from './BatchConfirmDialog';
import type { BatchActionContext, BatchActionDef, BatchRefusals } from './batchTypes';

/** State ResourceDataGrid hands the toolbar so the menu can build each action's
 *  context and run the generic dispatch. */
export interface BatchToolbarState {
  actions: BatchActionDef[];
  /** Ticked-row ids (used when not in all-matching mode). Numbers for
   *  integer-PK resources, strings for UUID-PK resources (delivery logs). */
  ids: Array<number | string>;
  /** All-matching engaged. */
  allMatching: boolean;
  /** Number the user is acting on (ids.length, or the grid total when all-matching). */
  count: number;
  /** Grid total for the current query. */
  matched: number | null;
  /** Current list query string (no leading `?`). */
  filterQuery: string;
  /** The grid's active tenant filter (see BatchActionContext.tenantFilterId). */
  tenantFilterId: number | null;
  /** Tenant per selected row (see BatchActionContext.rowTenantIds). */
  rowTenantIds: Array<number | null>;
  /** The selected rows the grid has loaded (see BatchActionDef.unavailableReason). */
  rows: readonly unknown[];
  /** Generic `/batch/` dispatch (snackbar + clear + invalidate handled upstream), with the
   *  action's opt-in refusal reporting. `filterQuery`, when given, replaces the grid's
   *  current one: the copy pinned when the action opened (see pinRelativeFilters). */
  runStandard: (
    action: string,
    label: string,
    selection: BatchSelection,
    params?: Record<string, unknown>,
    refusals?: BatchRefusals,
    filterQuery?: string,
  ) => Promise<void>;
  /** Clear the grid selection (for custom `run` handlers to call on success). */
  clearSelection: () => void;
  /** Engage "select all matching" mode (the inline banner's action). */
  onSelectAllMatching: () => void;
  /** True while a dispatch is in flight (disables the trigger). */
  isPending: boolean;
}

export function BatchActionsMenu({ batch }: { batch: BatchToolbarState }) {
  const { hasPerm } = usePermissions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [active, setActive] = useState<BatchActionDef | null>(null);
  const [dialogNode, setDialogNode] = useState<ReactNode>(null);
  // All matching only: the list query with its live time windows (`now-15m`) frozen
  // when the action opened, so the action's count, preview and apply see one set.
  // Undefined when there was nothing to freeze (an ids selection, no relative bound).
  const [pinnedQuery, setPinnedQuery] = useState<string | undefined>(undefined);

  const { actions, ids, allMatching, count, matched, filterQuery, tenantFilterId, rowTenantIds, rows, runStandard, clearSelection, isPending } = batch;

  // Only actions the operator may run (backend re-checks regardless).
  const visible = actions.filter((a) => !a.permission || hasPerm(a.permission));
  if (visible.length === 0 || count <= 0) return null;

  const selection: BatchSelection = allMatching ? { mode: 'all_matching' } : { mode: 'ids', ids };

  const closeAll = () => {
    setActive(null);
    setDialogNode(null);
  };

  const matchedPhrase =
    matched === null ? 'all matching' : `all ${matched.toLocaleString()} matching`;

  /** The generic dispatch, on the pinned query when there is one. */
  const dispatch = (def: BatchActionDef, params: Record<string, unknown> | undefined, pinned: string | undefined) => {
    if (!def.action) return Promise.reject(new Error(`Batch action "${def.key}" has no action key.`));
    return pinned === undefined
      ? runStandard(def.action, def.label, selection, params, def.refusals)
      : runStandard(def.action, def.label, selection, params, def.refusals, pinned);
  };

  const makeCtx = (def: BatchActionDef, pinned: string | undefined): BatchActionContext => ({
    selection,
    count,
    matched,
    matchedPhrase,
    filterQuery: pinned ?? filterQuery,
    tenantFilterId,
    // Only meaningful for an ids-mode selection; all-matching acts on rows the
    // client never saw.
    rowTenantIds: allMatching ? [] : rowTenantIds,
    close: closeAll,
    clearSelection,
    runStandard: (params) => dispatch(def, params, pinned),
  });

  const pick = (def: BatchActionDef) => {
    setAnchorEl(null);
    const frozen = allMatching ? pinRelativeFilters(filterQuery) : filterQuery;
    const pinned = frozen !== filterQuery ? frozen : undefined;
    setPinnedQuery(pinned);
    if (def.dialog) {
      setActive(def);
      setDialogNode(def.dialog(makeCtx(def, pinned)));
      return;
    }
    if (def.run) {
      void def.run(makeCtx(def, pinned));
      return;
    }
    // Default: confirm, then the generic dispatch.
    setActive(def);
    setDialogNode(null);
  };

  const confirmActive = active && !active.dialog && !active.run ? active : null;

  return (
    <>
      <Tooltip title="Batch actions">
        {/* span: a disabled button can't anchor a tooltip */}
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            disabled={isPending}
            aria-label="Batch actions"
          >
            <PlaylistAddCheckIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { maxWidth: 360 } }, list: { disabledItemsFocusable: true } }}
      >
        {visible.map((def) => {
          // All-matching acts on rows the client never saw: the backend decides there.
          const unavailable = def.unavailableReason?.(allMatching ? [] : rows) ?? null;
          return (
            <MenuItem
              key={def.key}
              // Disabled items only lose pointer events to CSS: never run one regardless.
              onClick={unavailable === null ? () => pick(def) : undefined}
              disabled={unavailable !== null}
              sx={{
                ...(def.destructive ? { color: 'error.main' } : {}),
                // A disabled action stays legible: its label dims, its reason does not.
                '&.Mui-disabled': { opacity: 1 },
                '&.Mui-disabled .MuiListItemIcon-root, &.Mui-disabled .MuiListItemText-primary': {
                  color: 'text.disabled',
                },
              }}
            >
              {def.icon && (
                <ListItemIcon sx={def.destructive ? { color: 'error.main' } : undefined}>
                  {def.icon}
                </ListItemIcon>
              )}
              <ListItemText
                primary={def.label}
                secondary={unavailable}
                secondaryTypographyProps={{ variant: 'caption', sx: { display: 'block', whiteSpace: 'normal' } }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Custom dialog (the action rendered its own node). */}
      {dialogNode}

      {/* Built-in confirm for `action`-only defs (with a reason field when required). */}
      {confirmActive && (
        <BatchConfirmDialog
          open
          title={confirmActive.label}
          message={confirmActive.confirmText?.(count) ?? `${confirmActive.label} ${count.toLocaleString()} record(s)?`}
          confirmLabel={confirmActive.label}
          destructive={confirmActive.destructive}
          reason={confirmActive.reason}
          reasonLabel={confirmActive.reasonLabel}
          onCancel={closeAll}
          onConfirm={({ reason }) => {
            const def = confirmActive;
            closeAll();
            if (def.action)
              void dispatch(def, reason ? { [def.reasonParam ?? 'reason']: reason } : undefined, pinnedQuery);
          }}
        />
      )}
    </>
  );
}
